import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal, FloatingActionButton, Modal } from '../components';
import { CalendarList } from '../components/calendar';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const CalendarListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const goBack = useGoBack('business-calendar');
  const { currentBusiness, user } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [calendars, setCalendars] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const companyCalendars = calendars.filter((c) => !!c.businessId);
  const personalCalendars = calendars.filter((c) => !c.businessId && c.userId === user?.id);


  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const loadCalendars = useCallback(async () => {
    if (!currentBusiness?._id) {
      setError('No business selected');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      // Server-side access filtering (calendar.service.ts's
      // resolveBusinessCalendarAccessFilter) already restricts business
      // calendars to what this user's general.manageCalendarBookings level
      // permits, and always includes their own personal calendars — the
      // split below is purely a client-side presentation grouping, not an
      // additional access boundary.
      const response = await apiService.getCalendars({
        businessId: currentBusiness._id,
      });

      if (response.statusCode === 1000) {
        // Handle both array and non-array responses, and empty arrays
        const calendarsArray = response.data
          ? (Array.isArray(response.data) ? response.data : [response.data])
          : [];
        setCalendars(calendarsArray);
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.list.loadFailed');
        setError(errorMessage);
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error loading calendars:', error);
      const errorMessage = error.message || t('calendar.list.loadFailed');
      setError(errorMessage);
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentBusiness, showNotification]);

  // Removed loadUpcomingEvents function - upcoming events section removed

  useEffect(() => {
    loadCalendars();
  }, [loadCalendars]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadCalendars();
  };

  const handleCalendarPress = (calendarId: string) => {
    navigateTo('business-calendar', calendarId, { calendarId });
  };

  const handleCalendarEdit = (calendarId: string) => {
    navigateTo('calendar-edit', calendarId);
  };

  const handleCalendarDelete = (calendarId: string) => {
    setCalendarToDelete(calendarId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!calendarToDelete || !currentBusiness?._id) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteCalendar(calendarToDelete, currentBusiness._id);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', t('calendar.list.deleteSuccess'));
        setShowDeleteModal(false);
        setCalendarToDelete(null);
        loadCalendars();
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.list.deleteFailed');
        showNotification('error', 'Error', errorMessage);
        setShowDeleteModal(false);
        setCalendarToDelete(null);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || t('calendar.list.deleteFailed'));
      setShowDeleteModal(false);
      setCalendarToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateBusinessCalendar = () => {
    navigateTo('calendar-create', undefined, { scope: 'business' });
  };

  const handleCreatePersonalCalendar = () => {
    navigateTo('calendar-create', undefined, { scope: 'personal' });
  };

  if (loading && calendars.length === 0) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onTabPress={handleTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent={null}
        formHeader={<FormHeader title="Calendars" onBack={goBack} />}
      >
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('calendar.list.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
      formHeader={<FormHeader title="Calendars" onBack={goBack} />}
    >
      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
        {/* Calendars Section */}
        {error && calendars.length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={loadCalendars}>
              <Text style={styles.retryButtonText}>{t('calendar.list.retry')}</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            <View style={styles.calendarsSection}>
              <View style={[styles.sectionHeaderRow, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.sectionTitle}>{t('calendar.list.companyCalendars')}</Text>
              </View>
              <CalendarList
                calendars={companyCalendars}
                loading={loading}
                refreshing={refreshing}
                onRefresh={handleRefresh}
                onCalendarPress={handleCalendarPress}
                onCalendarEdit={handleCalendarEdit}
                onCalendarDelete={handleCalendarDelete}
                showActions={true}
                emptyMessage={t('calendar.list.noCompanyCalendars')}
              />
            </View>
            <View style={styles.calendarsSection}>
              <View style={[styles.sectionHeaderRow, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.sectionTitle}>{t('calendar.list.myPersonalCalendar')}</Text>
              </View>
              <CalendarList
                calendars={personalCalendars}
                loading={loading}
                refreshing={refreshing}
                onCalendarPress={handleCalendarPress}
                onCalendarEdit={handleCalendarEdit}
                onCalendarDelete={handleCalendarDelete}
                showActions={true}
                emptyMessage={t('calendar.list.noPersonalCalendar')}
              />
            </View>
          </>
        )}
      </ScrollView>

      {Platform.OS === 'web' ? (
        <FloatingActionButton
          options={[
            {
              id: 'create-business-calendar',
              label: t('calendar.list.createBusinessCalendar'),
              icon: 'business-outline',
              color: colors.primary,
              onPress: handleCreateBusinessCalendar,
            },
            {
              id: 'create-personal-calendar',
              label: t('calendar.list.createPersonalCalendar'),
              icon: 'person-outline',
              color: colors.primary,
              onPress: handleCreatePersonalCalendar,
            },
          ]}
          mainIcon="add"
          mainColor={colors.primary}
        />
      ) : (
        <View style={styles.fabContainer}>
          <FloatingActionButton
            options={[
              {
                id: 'create-business-calendar',
                label: t('calendar.list.createBusinessCalendar'),
                icon: 'business-outline',
                color: colors.primary,
                onPress: handleCreateBusinessCalendar,
              },
              {
                id: 'create-personal-calendar',
                label: t('calendar.list.createPersonalCalendar'),
                icon: 'person-outline',
                color: colors.primary,
                onPress: handleCreatePersonalCalendar,
              },
            ]}
            mainIcon="add"
            mainColor={colors.primary}
          />
        </View>
      )}

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('calendar.list.deleteTitle')}
        message={t('calendar.list.deleteMessage')}
        confirmLabel={t('calendar.list.deleteConfirm')}
        cancelLabel={t('calendar.list.deleteCancel')}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setCalendarToDelete(null);
        }}
        loading={deleting}
        variant="danger"
      />

      {/* Removed Event Selection and Edit Modals - events removed from this screen */}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? spacing.xl * 3 : spacing.md, // Extra padding on mobile for FAB
  },
  sectionTitle: {
    ...typography.h3,
    color: colors.textDark,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  upcomingEventsSection: {
    marginBottom: spacing.xl,
  },
  calendarsSection: {
    marginBottom: spacing.md,
  },
  // Event card styles
  eventCard: {
    width: '100%',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderLeftWidth: 4,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      },
      default: {
        elevation: 1,
      },
    }),
  },
  eventCardContent: {
    flex: 1,
  },
  eventCardTime: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  eventCardTimeText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  eventCardTimeTo: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  eventCardTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
  },
  eventCardDescription: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginBottom: spacing.xs,
  },
  eventCardDate: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  eventCardMore: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginTop: spacing.xs,
  },
  eventCardMoreText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  // Event edit modal styles
  eventEditContainer: {
    paddingVertical: spacing.md,
  },
  eventEditSection: {
    marginBottom: spacing.lg,
  },
  eventEditLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  eventEditValue: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    lineHeight: 22,
  },
  eventEditTime: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
    marginTop: spacing.xs,
  },
  eventEditLink: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  actionBtn: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.secondary,
  },
  actionBtnPrimary: {
    backgroundColor: colors.primary,
  },
  actionBtnText: {
    ...typography.body,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  actionBtnPrimaryText: {
    ...typography.body,
    color: colors.white,
    fontFamily: typography.fontFamily.medium,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.md,
  },
  errorText: {
    ...typography.body,
    color: colors.error,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: 8,
  },
  retryButtonText: {
    ...typography.body,
    color: colors.white,
    fontWeight: '600',
  },
  fabContainer: {
    position: 'absolute',
    bottom: Platform.OS !== 'web' ? 70 : spacing.xl, // Account for bottom tabs (50px height + 8px padding + safe area)
    right: spacing.xl,
    zIndex: 1000,
  },
});

